/**
 * Validation schemas shared by API, web and the design tool for the PV engineering workflow.
 * User-facing messages are Vietnamese.
 */
import { z } from 'zod';
import { buildingsDataSchema } from './structure/building';
import { electricalDataSchema, structuresDataSchema } from './structure/schema';
import { bessDataSchema } from './bess';
import { omDataSchema } from './om';

const pos = (label: string, max = 1e7) =>
  z
    .number({ error: `${label} phải là số` })
    .positive(`${label} phải > 0`)
    .max(max);
const num = (label: string, min = -1e7, max = 1e7) =>
  z
    .number({ error: `${label} phải là số` })
    .min(min)
    .max(max);

/** Where a specification comes from — calculations must distinguish datasheet data from entered values. */
export const SPEC_SOURCES = ['DATASHEET', 'USER'] as const;
export type SpecSource = (typeof SPEC_SOURCES)[number];

export const pvModuleSpecSchema = z.object({
  widthMm: pos('Chiều rộng', 5000).optional(),
  lengthMm: pos('Chiều dài', 5000).optional(),
  weightKg: pos('Khối lượng', 500).optional(),
  pmaxW: pos('Pmax', 5000).optional(),
  vocV: pos('Voc', 2000).optional(),
  vmpV: pos('Vmp', 2000).optional(),
  iscA: pos('Isc', 100).optional(),
  impA: pos('Imp', 100).optional(),
  tempCoeffVoc: num('Hệ số nhiệt Voc', -1000, 0).optional(),
  tempCoeffVocUnit: z.enum(['PCT_PER_C', 'MV_PER_C']).optional(),
  tempCoeffPmaxPct: num('Hệ số nhiệt Pmax', -5, 0).optional(),
  tempCoeffVmpPct: num('Hệ số nhiệt Vmp', -5, 0).optional(),
  tempCoeffIscPct: num('Hệ số nhiệt Isc', -1, 1).optional(),
  maxSystemVoltageV: pos('Điện áp hệ thống tối đa', 3000).optional(),
  /** Maximum series fuse rating (module reverse-current limit), A */
  maxSeriesFuseA: pos('Định mức cầu chì chuỗi tối đa', 100).optional(),
});

export const inverterSpecSchema = z.object({
  acRatedW: pos('Công suất AC', 1e7).optional(),
  maxDcVoltageV: pos('Điện áp DC tối đa', 3000).optional(),
  startupVoltageV: pos('Điện áp khởi động', 3000).optional(),
  mpptMinV: pos('MPPT tối thiểu', 3000).optional(),
  mpptMaxV: pos('MPPT tối đa', 3000).optional(),
  mpptCount: z.number().int().min(1).max(50).optional(),
  stringsPerMppt: z.number().int().min(1).max(20).optional(),
  maxInputCurrentPerMpptA: pos('Dòng vào tối đa/MPPT', 1000).optional(),
  maxShortCircuitCurrentPerMpptA: pos('Dòng ngắn mạch tối đa/MPPT', 1000).optional(),
  maxPvPowerW: pos('Công suất PV tối đa', 1e7).optional(),
  phases: z.union([z.literal(1), z.literal(3)]).optional(),
  maxAcCurrentA: pos('Dòng AC tối đa', 1000).optional(),
  /** DC switch built into the inverter */
  dcSwitchIntegrated: z.boolean().optional(),
  batteryVoltageClass: z.enum(['LV', 'HV']).optional(),
  batteryVoltageMinV: pos('Điện áp pin tối thiểu', 1500).optional(),
  batteryVoltageMaxV: pos('Điện áp pin tối đa', 1500).optional(),
  maxBatteryChargeCurrentA: pos('Dòng sạc pin tối đa', 1000).optional(),
  maxBatteryDischargeCurrentA: pos('Dòng xả pin tối đa', 1000).optional(),
  maxParallelUnits: z.number().int().min(1).max(100).optional(),
});

export const batterySpecSchema = z.object({
  voltageClass: z.enum(['LV', 'HV']).optional(),
  nominalVoltageV: pos('Điện áp danh định', 1500).optional(),
  voltageMinV: pos('Điện áp tối thiểu', 1500).optional(),
  voltageMaxV: pos('Điện áp tối đa', 1500).optional(),
  capacityAh: pos('Dung lượng Ah', 10_000).optional(),
  capacityKwh: pos('Dung lượng kWh', 1000).optional(),
  usableCapacityKwh: pos('Dung lượng khả dụng', 1000).optional(),
  maxChargeCurrentA: pos('Dòng sạc tối đa', 1000).optional(),
  maxDischargeCurrentA: pos('Dòng xả tối đa', 1000).optional(),
  maxParallelUnits: z.number().int().min(1).max(100).optional(),
  communication: z.string().trim().max(200).optional(),
});

/** How an accessory quantity scales when several units are paralleled. */
export const PARALLEL_QTY_RULES = ['PER_UNIT', 'PER_EXTRA_UNIT', 'PER_SYSTEM'] as const;
export type ParallelQtyRule = (typeof PARALLEL_QTY_RULES)[number];
export const PARALLEL_QTY_RULE_LABEL: Record<ParallelQtyRule, string> = {
  PER_UNIT: 'Theo mỗi bộ',
  PER_EXTRA_UNIT: 'Theo mỗi bộ thêm (từ bộ thứ 2)',
  PER_SYSTEM: 'Một lần cho cả hệ',
};

/**
 * Parallel installation rules of an inverter / battery model (company configuration from the manufacturer's manual):
 * accessories required when 2+ units are installed in parallel. Absent = not configured (a warning is raised).
 */
export const parallelSpecSchema = z.object({
  supported: z.boolean().default(true),
  maxUnits: z.number().int().min(1).max(100).optional(),
  accessories: z
    .array(
      z.object({
        productId: z.uuid().nullable().default(null),
        label: z.string().trim().min(1, 'Nhập tên phụ kiện').max(200),
        unit: z.string().trim().min(1).max(30).default('cái'),
        rule: z.enum(PARALLEL_QTY_RULES),
        quantity: z.number().positive('Số lượng phải > 0').max(1000),
        note: z.string().trim().max(300).optional(),
      }),
    )
    .max(30)
    .default([]),
  note: z.string().trim().max(1000).optional(),
});
export type ParallelSpec = z.infer<typeof parallelSpecSchema>;

/** Structured technical specifications stored on a catalog product (Product.techSpecs). */
export const productTechSpecsSchema = z
  .object({
    source: z.enum(SPEC_SOURCES).default('DATASHEET'),
    datasheetRef: z.string().trim().max(300).optional(),
    pvModule: pvModuleSpecSchema.optional(),
    inverter: inverterSpecSchema.optional(),
    battery: batterySpecSchema.optional(),
    /** Inverter / battery: parallel installation accessories and limits */
    parallel: parallelSpecSchema.optional(),
  })
  .refine(
    (s) => !s.inverter?.mpptMinV || !s.inverter?.mpptMaxV || s.inverter.mpptMinV < s.inverter.mpptMaxV,
    {
      message: 'MPPT tối thiểu phải nhỏ hơn MPPT tối đa',
      path: ['inverter', 'mpptMinV'],
    },
  );
export type ProductTechSpecs = z.infer<typeof productTechSpecsSchema>;

// ---------------------------------------------------------------- design data (subset read by the server)

const point = z.tuple([z.number().finite(), z.number().finite()]);

export const OBSTACLE_KINDS = ['VENT', 'CHIMNEY', 'SKYLIGHT', 'EQUIPMENT', 'EXCLUSION', 'WALKWAY'] as const;

/** Canvas layout as saved by the design tool (pixels + pxPerMm). Unknown fields are kept untouched. */
export const layoutDataSchema = z
  .object({
    pxPerMm: z.number().positive().max(100),
    polygons: z
      .array(
        z
          .object({
            points: z.array(point).min(3).max(500),
            scale: z.number().positive().optional(),
            face: z.number().optional(),
            pitch: z.number().optional(),
          })
          .loose(),
      )
      .max(200),
    panels: z
      .array(z.object({ corners: z.array(point).length(4), polyIndex: z.number().int().min(0) }).loose())
      .max(20_000),
    panel: z
      .object({
        wMm: z.number().positive(),
        hMm: z.number().positive(),
        gapMm: z.number().min(0),
        wp: z.number().positive(),
        label: z.string().max(100),
      })
      .loose()
      .optional(),
    obstacles: z
      .array(
        z.object({
          id: z.string().max(60),
          kind: z.enum(OBSTACLE_KINDS),
          points: z.array(point).min(3).max(200),
          clearanceMm: z.number().min(0).max(10_000),
          label: z.string().max(100).optional(),
          /** Height above the roof (3D view), mm — optional */
          heightMm: z.number().min(0).max(30_000).optional(),
        }),
      )
      .max(500)
      .optional(),
    setbackMm: z.number().min(0).max(10_000).optional(),
    /** 3D design extension (all optional, versioned — see structure/schema.ts) */
    structures: structuresDataSchema.optional(),
    buildings: buildingsDataSchema.optional(),
    electrical: electricalDataSchema.optional(),
    /** O&M layout (walkways, safety, access, cleaning) — plan mm, see om.ts */
    om: omDataSchema.optional(),
    /** BESS site layout (plan mm) — equipment schedule / BOQ lines BESS:* */
    bess: bessDataSchema.optional(),
  })
  .loose();
export type LayoutData = z.infer<typeof layoutDataSchema>;

const optNum = (min: number, max: number) => z.number().min(min).max(max).nullable().optional();
const money = z
  .string()
  .regex(/^\d{1,15}$/, 'Số tiền không hợp lệ')
  .nullable()
  .optional();

export const stringAssignmentSchema = z.object({
  inverterIndex: z.number().int().min(0).max(100),
  mppt: z.number().int().min(0).max(50),
  strings: z
    .array(
      z.object({
        id: z.string().max(30),
        modules: z.number().int().min(1).max(100),
        roofId: z.string().max(60).optional(),
      }),
    )
    .max(20),
});

export const engineeringInputSchema = z.object({
  moduleProductId: z.uuid().nullable().default(null),
  inverterProductId: z.uuid().nullable().default(null),
  inverterCount: z.number().int().min(0).max(100).default(1),
  batteryProductId: z.uuid().nullable().default(null),
  batteryCount: z.number().int().min(0).max(100).default(0),
  conditions: z
    .object({
      minCellTempC: z.number().min(-50).max(50),
      maxCellTempC: z.number().min(0).max(100),
      assumed: z.boolean().default(true),
    })
    .default({ minCellTempC: 10, maxCellTempC: 70, assumed: true }),
  /** Chosen string configuration (null = not configured yet) */
  strings: z
    .object({
      assignments: z.array(stringAssignmentSchema).max(500),
      unassignedModules: z.number().int().min(0),
    })
    .nullable()
    .default(null),
  mountingSystemId: z.string().max(60).nullable().default(null),
  mountingParams: z
    .object({
      railStockLengthMm: optNum(500, 12_000),
      maxSupportSpacingMm: optNum(200, 5000),
      railEndOverhangMm: optNum(0, 1000),
      endCantileverMm: optNum(0, 2000),
    })
    .default({}),
  cables: z
    .object({
      dcRoutePerStringM: optNum(0, 10_000),
      acRouteM: optNum(0, 10_000),
      peRouteM: optNum(0, 10_000),
      acConductors: optNum(1, 10),
      allowancePct: optNum(0, 100),
    })
    .default({}),
  mc4PairsPerString: optNum(0, 20),
  /** Cable / protection sizing parameters (null = default of sizing.ts) */
  sizing: z
    .object({
      dcVoltageDropPct: optNum(0.1, 10),
      acVoltageDropPct: optNum(0.1, 10),
      batteryVoltageDropPct: optNum(0.1, 10),
      dcDerating: optNum(0.1, 1.5),
      acDerating: optNum(0.1, 1.5),
      threePhasePoles: z
        .union([z.literal(3), z.literal(4)])
        .nullable()
        .optional(),
      batteryRouteM: optNum(0, 1000),
      confirmed: z.boolean().default(false),
    })
    .default({ confirmed: false }),
  manualLines: z
    .array(
      z.object({
        id: z.string().max(30),
        productId: z.uuid().nullable(),
        label: z.string().trim().min(1, 'Nhập tên vật tư').max(250),
        unit: z.string().trim().min(1).max(30),
        quantity: z.number().min(0).max(1e7),
        category: z.enum(['PV', 'MOUNTING', 'DC', 'AC', 'OTHER']),
        note: z.string().max(500).optional(),
      }),
    )
    .max(200)
    .default([]),
  materialMap: z.record(z.string().max(60), z.uuid()).default({}),
  overrides: z
    .record(
      z.string().max(80),
      z.object({
        quantity: z.number().min(0).max(1e7),
        reason: z.string().trim().min(1, 'Nhập lý do điều chỉnh').max(500),
        by: z.string().max(150).optional(),
        at: z.string().max(40).optional(),
        basisHash: z.string().max(20),
      }),
    )
    .default({}),
  pricing: z
    .object({ laborPerKwp: money, transport: money, contingencyPct: optNum(0, 100), discount: money })
    .default({}),
});
export type EngineeringInput = z.infer<typeof engineeringInputSchema>;

export const designDraftSchema = z.object({
  layout: layoutDataSchema.nullable().default(null),
  engineering: engineeringInputSchema.default(() => engineeringInputSchema.parse({})),
});
export type DesignDraft = z.infer<typeof designDraftSchema>;
